<template>
  <router-link :to="detailLink" data-testid="card-item" class="no-underline" @click.native.prevent="trackItemOnClick">
    <BlCardMv
      :shadow="false"
      :class="!isHideBorder && 'border border-solid border-[#E0E0E0]'"
      class="card-item flex flex-col h-full rounded"
    >
      <div class="relative">
        <BlProductImageAv
          :image-url="`${optimizeImageWidth(course.images[0].url, 320)}.webp`"
          :image-alt="courseName"
          is-lazy-load
          image-placeholder="https://s2.bukalapak.com/attachment/237412/placeholder.png"
          size="extra-large"
          object-fit="cover"
          class="card-item__image w-full h-[98px] rounded-b-none"
        />
      </div>

      <BlFlexContainerMv class="bg-white rounded-b" padding="8px" direction="column" justify-content="space-between">
        <BlCapsuleAv v-if="courseTypeWording" class="w-max ph-4 mb-8" variation="informational">{{ courseTypeWording }}</BlCapsuleAv>
        <BlTextAv :tag="titleTag" variation="caption-12" :ellipsis="3" class="card-item__title leading-4 qa-card-name">
          {{ courseName }}
        </BlTextAv>

        <div>
          <BlTextAv variation="body-14" weight="bold" class="mt-8 qa-card-price">
            {{ course.discount.original_price | money }}
          </BlTextAv>

          <BlFlexContainerMv
            direction="row"
            align-items="center"
            class="mt-8 qa-rating-and-review"
            :class="!courseRating.value && 'invisible'"
          >
            <BlFlexItemAv v-if="courseRating.value" class="flex items-center justify-center space-x-[2px]">
              <IcoStar size="small" color="#FFA726" class="w-[12px] h-[12px]" />
              <BlTextAv color="secondary" variation="caption-10" class="qa-rating-value" style="font-size: 10px">
                {{ formatRatingValue }}
              </BlTextAv>
            </BlFlexItemAv>
            <div class="divider mr-4 ml-4"></div>
            <BlFlexItemAv v-if="courseRating.count" class="flex items-start justify-center">
              <BlTextAv color="secondary" variation="caption-10" class="qa-review-value">
                Diulas {{ course.rating.count | delimiter }}
              </BlTextAv>
            </BlFlexItemAv>
          </BlFlexContainerMv>
        </div>
      </BlFlexContainerMv>
    </BlCardMv>
  </router-link>
</template>

<script>
import {money, delimiter} from '@bukalapak/toolbox-helper/number-helper';
import {IcoStar} from '@bukalapak/bazaar-visual-next';
import {BlProductImageAv, BlCardMv, BlCapsuleAv} from '@bukalapak/bazaar-mweb-latest';
import {COURSE_TYPE} from '../../config/app';
import {generateURLString, encodeURI} from 'shared_js/coupon-deals';
import {optimizeImageWidth} from 'shared_js/image-helpers';
import trackerGenerator from '../../mixins/tracker-generator';
import {TRACKER_EVENT_VP_EDU_KARIER_SECTION_CLICK} from '../../config/tracker-events';

export default {
  name: 'CardItem',
  filters: {money, delimiter},
  components: {IcoStar, BlProductImageAv, BlCardMv, BlCapsuleAv},
  mixins: [trackerGenerator],
  props: {
    course: {
      type: Object,
      required: true,
    },
    sectionName: {
      type: String,
      required: true,
    },
    referrer: {
      type: String,
      default: null,
    },
    isHideBorder: {
      type: Boolean,
      default: false,
    },
    titleTag: {
      type: String,
      default: 'p',
    },
  },
  computed: {
    courseRating() {
      return {
        value: this.course.rating?.value ?? 0,
        count: this.course.rating?.count ?? 0,
      };
    },
    formatRatingValue() {
      return (Math.round(this.courseRating.value * 100) / 100).toFixed(1);
    },
    courseName() {
      const numberOfStar = (this.course.name.substr(0, 2).match(/\*/g) || []).length;
      return this.course.name.slice(numberOfStar);
    },
    detailLink() {
      let referrer;
      const referrerMapper = {
        search: `edu_karier_search | ${this.$route.query.keyword}`,
        merchant: 'edu_karier_merchant',
        'closest-course': 'edu_karier_kelas_terdekat',
        'dynamic-section': this.$route.params.section_slug,
      };

      const predefinedReferrer = referrerMapper[this.$route.name] || this.$route.name;

      if (this.referrer) referrer = this.referrer;
      else if (predefinedReferrer) referrer = predefinedReferrer;

      const queryParams = {
        ...this.$route.query,
        referrer: referrer,
      };
      const url = generateURLString(`${this.courseName} ${this.course.id}`);
      return `/${url}?${encodeURI(queryParams)}`;
    },
    courseTypeWording() {
      if (!this.course.course_type) return '';
      return COURSE_TYPE[this.course.course_type] || '';
    },
  },
  methods: {
    optimizeImageWidth,
    trackItemOnClick() {
      const trackerPayload = {
        sku_id: `${this.course.id}`,
        course_title: this.course.name,
        lpk_name: this.course.merchant_name,
        section_name: this.sectionName,
      };
      this.mixinSendTracker(TRACKER_EVENT_VP_EDU_KARIER_SECTION_CLICK, trackerPayload);
    },
  },
};
</script>

<style scoped>
.divider {
  height: 8px;
  width: 1px;
  background-color: #e8eaef;
}
</style>
